import { ref } from 'vue';

const DEBOUNCE_DELAY = 300;
const MIN_QUERY_LENGTH = 3;
const SUGGESTION_DELAY = 200;

/**
 * Composable for managing address autocomplete suggestions
 */
export function useAddressAutocomplete(mapboxToken) {
  const addressSuggestions = ref([]);
  const showSuggestions = ref(false);
  const selectedSuggestionIndex = ref(-1);
  let searchTimeout = null;

  const fetchAddressSuggestions = async (query) => {
    if (!query.trim() || query.length < MIN_QUERY_LENGTH || !mapboxToken) {
      addressSuggestions.value = [];
      showSuggestions.value = false;
      return;
    }

    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${mapboxToken}&limit=5&types=address,poi,place`
      );

      const data = await response.json();

      if (data.features && data.features.length > 0) {
        addressSuggestions.value = data.features;
        showSuggestions.value = true;
        selectedSuggestionIndex.value = -1;
      } else {
        addressSuggestions.value = [];
        showSuggestions.value = false;
      }
    } catch (error) {
      console.error('Error fetching address suggestions:', error);
      addressSuggestions.value = [];
      showSuggestions.value = false;
    }
  };

  const onAddressInput = (addressValue, callback) => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    if (!addressValue.trim()) {
      addressSuggestions.value = [];
      showSuggestions.value = false;
      return;
    }

    searchTimeout = setTimeout(async () => {
      await fetchAddressSuggestions(addressValue);
      if (callback) callback();
    }, DEBOUNCE_DELAY);
  };

  const onAddressBlur = () => {
    setTimeout(() => {
      showSuggestions.value = false;
    }, SUGGESTION_DELAY);
  };

  const selectNextSuggestion = () => {
    if (addressSuggestions.value.length > 0) {
      selectedSuggestionIndex.value = Math.min(
        selectedSuggestionIndex.value + 1,
        addressSuggestions.value.length - 1
      );
    }
  };

  const selectPreviousSuggestion = () => {
    if (addressSuggestions.value.length > 0) {
      selectedSuggestionIndex.value = Math.max(selectedSuggestionIndex.value - 1, -1);
    }
  };

  const clearSuggestions = () => {
    addressSuggestions.value = [];
    showSuggestions.value = false;
    selectedSuggestionIndex.value = -1;
  };

  const cleanup = () => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
      searchTimeout = null;
    }
  };

  return {
    addressSuggestions,
    showSuggestions,
    selectedSuggestionIndex,
    onAddressInput,
    onAddressBlur,
    selectNextSuggestion,
    selectPreviousSuggestion,
    clearSuggestions,
    cleanup
  };
}

